import AttachmentUploader, {
  AttachmentUploaderRef,
} from "@/components/attachment-uploader";
import { Button } from "@/components/ui/button";
import { useCreateResourceFileMutation } from "@/hooks/create-resource-file.hook";
import { useEditionById } from "@/hooks/edition-by-id.hook";
import { useResourceLibrary } from "@/hooks/resource-library.hook";
import { forceFileDownload } from "@/lib/utils";
import { mapInscriptionFormTypeToLabel } from "@/mappers/editions";
import { ApplyForEditionRequestForm } from "@/requests/applications/apply-for-edition.request";
import { getEditionResourceById } from "@/requests/libraries/get-edition-resource-by-id.request";
import { InscriptionForm } from "@/types/edition";
import { DocumentText1 } from "iconsax-react";
import { FC, useRef } from "react";
import { useParams } from "react-router-dom";

type EnrollmentFileUploaderProps = {
  form: InscriptionForm;
  onFileUpload: (attachment: ApplyForEditionRequestForm) => void;
  onFileRemove: (attachment: ApplyForEditionRequestForm) => void;
};

/**
 * Component to upload enrollment files for an edition.
 *
 * TODO:
 * - Add error handling
 * - Add loading states
 */
const EnrollmentFileUploader: FC<EnrollmentFileUploaderProps> = (props) => {
  const uploaderRef = useRef<AttachmentUploaderRef>(null);

  const { editionId = "" } = useParams();

  const { data: resourceLibrary } = useResourceLibrary();

  const { data: currentEdition } = useEditionById({
    companyId: resourceLibrary?.owner.id || "",
    editionId,
  });

  const fileMutation = useCreateResourceFileMutation();

  // Executes after the file is uploaded successfully
  const onFileUpload = (attachment: ApplyForEditionRequestForm) => {
    props.onFileUpload(attachment);
  };

  const onFileRemove = (attachment: ApplyForEditionRequestForm) => {
    props.onFileRemove(attachment);
  };

  // Remove the file loaded. UI only.
  const clearFile = () => {
    uploaderRef.current?.clearAcceptedFile();
  };

  const downloadFile = () => {
    getEditionResourceById({
      editionId: currentEdition?.id || "",
      resourceLibraryId: currentEdition?.libraryId || "",
      resourceId: props.form.resourceId,
    }).then((resource) => {
      forceFileDownload(resource.signedUrl);
    });
  };

  return (
    <div>
      <h6 className="mb-3 text-xs font-medium text-[#4F5D6D]">
        {mapInscriptionFormTypeToLabel(props.form.formType)}
      </h6>

      <AttachmentUploader
        ref={uploaderRef}
        afterAcceptFile={(file) => {
          fileMutation.mutate(
            {
              companyId: resourceLibrary?.owner.id || "",
              resourceLibraryId: resourceLibrary?.id || "",
              file: file,
              name: file.name,
            },
            {
              onSuccess: (data) =>
                onFileUpload({
                  resourceId: data.id,
                  formType: props.form.formType,
                  name: file.name,
                }),
              onError: () => {
                clearFile();
              },
            },
          );
        }}
        isLoading={fileMutation.isPending}
        afterRemoveFile={() => {
          onFileRemove({
            resourceId: props.form.resourceId,
            formType: props.form.formType,
            name: props.form.name,
          });
        }}
        buttons={
          <Button
            variant={"warning"}
            size={"sm"}
            onClick={() => downloadFile()}
          >
            <DocumentText1 size={20} />
            Descargar formato
          </Button>
        }
      />
    </div>
  );
};

export default EnrollmentFileUploader;
